import { useContext, useRef, useState, useEffect } from "react";
import { useStyles } from "./ActionPlanCommentModal.styles";
import { Box, InputLabel, TextField, Typography } from "@mui/material";
import { Form, Formik, FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { addActionPlanCommentFormValidationSchema } from "validations/schemas";
import MainModal from "components/MainModal";
import { useRCAMainStyles } from "pages/Corporate/RCA/RCA.styles";
import DoneIcon from "@mui/icons-material/Done";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { ActionPlanEntity } from "entities/RCA/ActionPlanEntity/ActionPlanEntity";
import { IActionPlanCommentModal, IActionPlanCommentModalFormValues } from "./ActionPlanCommentModalInterfaces";

const ActionPlanCommentModal = ({
    open,
    onClose,
    onComplete,
    onError,
    actionPlanId,
    comment,
}: IActionPlanCommentModal) => {
    const classes = useStyles();
    const mainClasses = useRCAMainStyles();
    const [isSaving, setIsSaving] = useState(false);
    const { t } = useTranslation();
    const formRef = useRef<FormikProps<IActionPlanCommentModalFormValues>>(null);
    const { currentUser, isLoadingCurrentUser } = useContext(CorporateContext);
    const { createActionPlanComment } = ActionPlanEntity.InsertActionPlanComment();
    const { updateActionPlanComment } = ActionPlanEntity.UpdateActionPlanComment();

    const isEditMode = !!comment;

    const initFormValues: IActionPlanCommentModalFormValues = {
        description: comment?.description || "",
    };

    const handleOnClose = () => {
        onClose();
    };

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                const textareaById = document.getElementById("outlined-description-input") as HTMLTextAreaElement;

                if (textareaById) {
                    textareaById.focus();
                    if (isEditMode && comment?.description) {
                        const length = comment.description.length;
                        textareaById.setSelectionRange(length, length);
                    }
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [open, isEditMode, comment]);

    const handleSubmit = async (values: IActionPlanCommentModalFormValues) => {
        setIsSaving(true);

        try {
            let response;
            if (isEditMode) {
                // Edit mode
                response = await updateActionPlanComment({
                    variables: {
                        actionPlanCommentId: comment!.action_plan_comment_id,
                        changes: {
                            description: values.description,
                        },
                    },
                });
            } else {
                // Add mode
                response = await createActionPlanComment({
                    variables: {
                        actionPlanComment: {
                            description: values.description,
                            action_plan_id: actionPlanId,
                            user_id: currentUser?.user_id,
                        },
                    },
                });
            }

            await onComplete(isEditMode ? values.description : undefined);
            setIsSaving(false);
            if (response) {
                onClose();
            }
        } catch (error) {
            setIsSaving(false);
            if (onError) {
                onError();
            }
            return;
        }
    };

    const modalTitle = isEditMode
        ? t("rca.action_plans.action_plan_details.comments.edit_comment_modal.title")
        : t("rca.action_plans.action_plan_details.comments.add_comment_modal.title");

    const okButtonText = isEditMode
        ? t("rca.action_plans.action_plan_details.comments.edit_comment_modal.update")
        : t("rca.action_plans.action_plan_details.comments.add_comment_modal.create");

    const descriptionLabel = isEditMode
        ? t("rca.action_plans.action_plan_details.comments.edit_comment_modal.description")
        : t("rca.action_plans.action_plan_details.comments.add_comment_modal.description");

    const cancelButtonText = isEditMode
        ? t("rca.action_plans.action_plan_details.comments.edit_comment_modal.cancel")
        : t("rca.action_plans.action_plan_details.comments.add_comment_modal.cancel");

    return (
        <MainModal
            isOpen={open}
            onClose={handleOnClose}
            title={modalTitle}
            onOkButtonClick={() => {
                if (formRef.current) {
                    formRef.current.handleSubmit();
                }
            }}
            disableOkButton={isSaving}
            disableEscapeKeyDown
            disableCancelButton={isSaving}
            okButton={
                <>
                    <DoneIcon />
                    {okButtonText}
                </>
            }
            cancelButton={cancelButtonText}
            contentClassName={classes.content}
        >
            <div className={classes.form}>
                <Formik
                    innerRef={formRef}
                    initialValues={initFormValues}
                    validationSchema={addActionPlanCommentFormValidationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={isEditMode}
                >
                    {({ isSubmitting, errors, touched, values, handleChange }) => {
                        const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
                            if (event.key === "Enter" && !event.shiftKey && !isSaving && !isSubmitting) {
                                event.preventDefault();
                                if (formRef.current) {
                                    formRef.current.handleSubmit();
                                }
                            }
                        };

                        return (
                            <Form className={mainClasses.contentForm}>
                                <Box className={mainClasses.contentFormBox}>
                                    <InputLabel id="outlined-description-label">
                                        <Typography variant="subtitle2" className={mainClasses.contentFormLabel}>
                                            {descriptionLabel}
                                        </Typography>
                                    </InputLabel>
                                    <TextField
                                        disabled={isSaving || isSubmitting || (isLoadingCurrentUser && !isEditMode)}
                                        aria-labelledby="outlined-description-label"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        id="outlined-description-input"
                                        className={mainClasses.contentFormMultitextField}
                                        name="description"
                                        type="text"
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description && t(errors.description)}
                                    />
                                </Box>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </MainModal>
    );
};

export default ActionPlanCommentModal;
